import type { WorkspaceStatus } from '@/lib/types/workspace';
import { extractHostname, isLocalHost } from '@/lib/utils/workspace';
import type { WorkspaceBackendSnapshot } from './WorkspaceStatusProvider';

export type LegacyStatusPollFailureMode = 'timeout' | 'http_503' | 'http_error';

export interface LegacyStatusPollResult {
  status: WorkspaceStatus | null;
  failureMode: LegacyStatusPollFailureMode | null;
}

/**
 * Direct simulation-status polling for workspace images that predate the
 * workspace status stream.
 *
 * Modern workspaces use `/workspace/status/stream` plus `/sim/status/stream`;
 * this exists only so the current webapp can still observe older already-
 * running workspaces. Keep all callers behind a missing-status-stream check.
 *
 * :param workspaceUrl: Workspace URL or hostname.
 * :param workspaceToken: JWT token with workspace audience, required for cloud.
 * :param silentErrors: Suppress expected compatibility-probe failures.
 * :return: Parsed sim status and the failure mode, if any.
 */
export async function getLegacySimStatus(
  workspaceUrl: string,
  workspaceToken?: string,
  silentErrors = false
): Promise<LegacyStatusPollResult> {
  const hostname = extractHostname(workspaceUrl);
  const isLocal = isLocalHost(hostname);
  const baseUrl = isLocal ? `http://${hostname}:8081` : `https://${hostname}`;
  const url = `${baseUrl}/sim/status`;

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (workspaceToken && !isLocal) {
    headers.Authorization = `Bearer ${workspaceToken}`;
  }

  const timeoutMs = isLocal ? 2000 : 5000;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal: AbortSignal.timeout(timeoutMs),
    });

    if (!response.ok) {
      if (!silentErrors) {
        console.warn(`[workspace] legacy /sim/status returned ${response.status} (${response.statusText})`);
      }
      return { status: null, failureMode: response.status === 503 ? 'http_503' : 'http_error' };
    }

    return { status: (await response.json()) as WorkspaceStatus, failureMode: null };
  } catch (err) {
    if (!silentErrors) {
      console.warn('[workspace] legacy /sim/status fetch failed:', err instanceof Error ? err.message : err);
    }
    const isTimeout = err instanceof DOMException && err.name === 'TimeoutError';
    return { status: null, failureMode: isTimeout ? 'timeout' : 'http_error' };
  }
}

function canStreamLegacyStatus(status: WorkspaceStatus | null): boolean {
  return (
    status?.healthy === true && (status.state === 'playing' || status.state === 'paused' || status.state === 'stopped')
  );
}

/**
 * Wrap a legacy `/sim/status` payload so the modern WorkspaceStatusProvider
 * shape stays stable for downstream consumers.
 *
 * :param status: Direct `/sim/status` payload, if reachable.
 * :param reachable: Whether the direct sim status endpoint returned a payload.
 * :return: Synthetic control-plane snapshot with only legacy-observable fields.
 */
export function legacyCompatSnapshot(status: WorkspaceStatus | null, reachable: boolean): WorkspaceBackendSnapshot {
  const statusCanStream = canStreamLegacyStatus(status);
  const simStatus =
    !reachable || status === null
      ? 'absent'
      : status.state === 'starting'
        ? 'starting'
        : statusCanStream
          ? 'ready'
          : 'stopped';

  return {
    version: 0,
    seq: Date.now(),
    server_ts_ms: Date.now(),
    workspace_server: { status: 'unavailable' },
    jupyter: { status: 'unknown' },
    sim: {
      status: simStatus,
      reachable,
      status_payload: status,
    },
    streaming: { status: statusCanStream ? 'ready' : status?.state === 'starting' ? 'starting' : 'unknown' },
    rerun: { status: statusCanStream ? 'unknown' : 'unavailable' },
  };
}
